import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { contextMenu, defaultColDef, gridSettings, sideBar, statusBar } from "./common/grid";
import { PdfReport } from "./common/pdfreport";
import { LoadingSuspense } from "./common/loaders/loadingsuspense";
import { useSelector } from "react-redux";
import { authSelector } from "./reducers";
import { fieldGetter, numberFormatterFraction } from "./common/utils";
import { restoreIconString } from "./common/icons";

let loaded = false;


const fixedFormatter = params => typeof params.value === "number"
  ? params.value.toFixed(2)
  : params.value;


/**
 * @param {{ value: Date }} params
 * @return {string}
 */
const finnishDateFormatter = ({ value }) => value?.toLocaleDateString && value.toLocaleDateString("fi-FI");


function clarionDateGetter(params) {
  const convertedDate = new Date(1800, 11, 28); // Clarion Epoch
  const plusDays = fieldGetter(params); // Number of days from Clarion Epoch
  convertedDate.setDate(convertedDate.getDate() + plusDays);
  return convertedDate;
  //return convertedDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
}



function parsePropertyWithIndex(str) {
  const match = str.match(/^([^[\]]+)\[(\d+)]$/);
  if (!match) return null; // handle invalid format

  const [, prop, idx] = match;
  return { prop, idx: Number(idx) };
}


// used in production inside ePalkat iframe (JSON data is sent to HR2 iframe using JavaScript window.postMessage generated with Clarion)
// Required headers for ePalkat iframe:
// Content-Security-Policy: default-src 'self' 'unsafe-inline' 'unsafe-eval' phantasos.heeros.com:9014 *.esalary.fi fonts.googleapis.com fonts.gstatic.com; frame-ancestors 'self' *.esalary.fi;
// https://epalkat.heeros.com/lomajaksw
// https://phantasos.heeros.com:8023/pljrpraj (pilotti)
export function Grid() {
  const lng = new URL(document.location).searchParams.get("lng") ?? "fi";
  const { t } = useTranslation("grid", {lng});
  const tt = useTranslation("translations", {lng}).t;
  const [state, setState] = useState({});
  const gridRef = useRef();

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);

  useEffect(() => {
    if (!loaded) {
      loaded = true;
      //console.log(222, "grid loaded");

      window.onmessage = e => {
        //console.log("Grid received message", e);
        setState(() => {
          const data = e.data;
          // https://www.ag-grid.com/react-data-grid/cell-data-types/
          for (const item in data.columnDefs) { // add valueFormatter to number columns
            const colDef = data.columnDefs[item];
            if (colDef.filter === "agNumberColumnFilter") {
              colDef.valueFormatter = numberFormatterFraction; // fixedFormatter
              colDef.type = "rightAligned";
            }
            else if (colDef.filter === "agIntegerColumnFilter") { // self-defined value for plain integer columns without formatter
              colDef.filter = "agNumberColumnFilter";
              colDef.type = "rightAligned";
            }
            else if (colDef.filter === "agArrayColumnFilter") { // self-defined value for picking a field from JSON array, for example field = "TXT[2]"
              const f = parsePropertyWithIndex(colDef.field);
              if (f !== null) {
                // eslint-disable-next-line no-extra-boolean-cast
                colDef.valueGetter = ({ colDef, data }) => !!data ? data[f.prop][f.idx] : null;
                colDef.cellStyle = { whiteSpace: "normal" }; // wordBreak
                colDef.wrapText = true; // https://www.ag-grid.com/react-data-grid/row-height/
                colDef.autoHeight = true;
              }
            }
            else if (colDef.filter === "agDateColumnFilter") {
              colDef.cellClass = "dateType";
              colDef.valueGetter = clarionDateGetter;
              //colDef.valueFormatter = dateFormatter;
              colDef.valueFormatter = finnishDateFormatter;
            }
            else if (colDef.filter === "agBooleanColumnFilter") { // self-defined value for true/false readonly checkbox columns without formatter
              colDef.cellDataType = "boolean";
              colDef.filter = "agSetColumnFilter";
              colDef.valueGetter = ({ colDef, data }) => !!data && !!Number(data[colDef.field]);
            }
          }
          return data;
        });
      };

      parent.postMessage("gridReady", "*");
    }

  }, []);

  const getContextMenuItems = params => {
    let items = ["separator"];
    items = items.concat(contextMenu(tt, params, setReport, {
      title: tt("Title"),
      companyname: tt("Company"),
    }));

    items.push({
      name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
      action: params => {
        params.api.resetColumnState();
        params.api.resetColumnGroupState();
      },
      icon: restoreIconString,
    });

    return items;
  };

  //console.log("gridState", state);

  return <>
    { report !== null && <PdfReport json={report} onCloseReport={closeReport} lng={lng} /> }
    <AgGridReact
      ref={gridRef}
      {...gridSettings}
      defaultColDef={defaultColDef}
      sideBar={sideBar}
      statusBar={statusBar}
      getContextMenuItems={getContextMenuItems}
      getLocaleText={params => t(params.key)}
      className="ag-theme-material styles-ag vh-100 m-0"
      animateRows
      suppressCellFocus
      enableCellTextSelection
      columnDefs={state.columnDefs}
      rowData={state.rowData}
      onFirstDataRendered={params => params.api.autoSizeAllColumns()}
      groupDefaultExpanded={1}
      suppressAggFuncInHeader
      {...state.gridOptions}
      domLayout="autoHeight"
    />
  </>;
}


// for testing Grid() inside iframe
// http://localhost:5173/grid-frame
export function GridFrame() {

  const columnDefs = [
    {
      field: "name",
    },
    {
      field: "age",
      filter: "agNumberColumnFilter",
    },
    {
      field: "hobby[0]",
      filter: "agArrayColumnFilter",
    },
  ];

  const rowData = [
    {
      name: "Me",
      age: 22.1254567,
      hobby: ["Lorem ipsum dolor sit amet, consectetur adipiscing elit,\nsed do eiusmod tempor incididunt ut labore et dolore magna aliqua."],
    },
    {
      name: "You",
      age: 44.89234798725,
      hobby: ["But I must explain to you how all this mistaken idea of denouncing pleasure and praising pain was born\nand I will give you a complete account of the system."],
    },
  ];

  const loaded = ({ target }) => {
    console.log(111, "iframe loaded");
    window.onmessage = e => {
      console.log(111, "parent received message", e);
      if (e.data === "gridReady") {
        target.contentWindow.postMessage({columnDefs, rowData});
      }
    };
  };


  return <iframe onLoad={loaded} style={{height: "75vh", width: "100%"}} src={"/ag-grid?lng=fi"} />;
}


/*
https://epalkat.heeros.com/AktivoiHR
https://epalkat.heeros.com/Kumpyritykset_Frm
https://epalkat.heeros.com/LajistoTaulu
https://epalkat.heeros.com/Mallitaulukko_Frm
https://epalkat.heeros.com/autojono
https://epalkat.heeros.com/Lomakerrointaulu_Frm
https://epalkat.heeros.com/Automaattorit
https://epalkat.heeros.com/Kaannostaulu_Frm
https://epalkat.heeros.com/Ayliitot_Frm

https://epalkat.heeros.com/lomajaksw
https://epalkat.heeros.com/grid
https://up.esalary.fi
https://epalkat-dev.heeros.com:8106/privitframe
https://phantasos.heeros.com:9014/privitframe?companynumber=7519

X-Frame-Options: SAMEORIGIN
*/
export function EpalkatFrame({ path = "privitframe" }) {
  const auth = useSelector(authSelector);
  const [loaded, setLoaded] = useState(false);

  if (path === "salarySheet" && auth.company?.salarySheet) {
    path = auth.company.salarySheet;
  }

  return <>
    {!loaded && <LoadingSuspense />}
    <iframe 
      style={{height: "100vh", width: "100%"}} 
      src={`https://presalary.esalary.fi/${path}?token=${auth.access_token}`} 
      onLoad={() => setLoaded(true)} 
      />
  </>;
}
